<template>
  <div class="company-data">
    <sticky-header :title="$t('companyForm.companyData')">
      <btn
        type="success"
        slot="ui"
        @click.native="save">
        {{ $t('common.ui.save') }}
      </btn>
    </sticky-header>

    <block
      :title="$t('companyForm.userData')"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label" for="user_name">{{ $t('companyForm.name') }}</label>
        <input
          slot="field"
          name="user_name"
          v-model="user.name"
          :class="{ 'is-invalid': this.errors.indexOf('user_name') > -1 }"
          @keyup="clearError('user_name')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="user_email">{{ $t('companyForm.email') }}</label>
        <input
          slot="field"
          name="user_email"
          v-model="user.email"
          :class="{ 'is-invalid': this.errors.indexOf('user_email') > -1 || this.errors.indexOf('user_email_taken') > -1 }"
          @keyup="clearError('user_email');clearError('user_email_taken');"
          autocomplete="off"
          type="email" />
          <small
            v-if="this.errors.indexOf('user_email') > -1"
            class="note"
            slot="note">
            {{ $t('companyForm.emailIsInvalid') }}
          </small>
          <small
            v-if="this.errors.indexOf('user_email_taken') > -1"
            class="note"
            slot="note">
            {{ $t('companyForm.emailIsTaken') }}
          </small>
          <small
            v-if="!user.confirmed"
            class="note text-danger"
            slot="note">
            <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.confirmEmailAddressWorker') }}
          </small>
          <small
            v-if="!(this.errors.indexOf('user_email') > -1 || this.errors.indexOf('user_email_taken') > -1)"
            class="note"
            slot="note">
            {{ $t('companyForm.thisEmailWillBeUsedToLogin') }}
          </small>
      </field>

      <field slot="content">
        <label slot="label" for="user_phone">{{ $t('companyForm.phone') }}</label>
        <input
          slot="field"
          name="user_phone"
          v-model="user.phone"
          :class="{ 'is-invalid': this.errors.indexOf('user_phone') > -1 }"
          @keyup="clearError('user_phone')"
          autocomplete="off"
          type="tel" />
      </field>

      <field slot="content">
        <label slot="label" for="user_password">{{ $t('companyForm.password') }}</label>
        <input
          slot="field"
          name="user_password"
          v-model="user.password"
          :class="{ 'is-invalid': this.errors.indexOf('user_password') > -1 }"
          @keyup="clearError('user_password')"
          autocomplete="off"
          spellcheck="false"
          type="password" />
        <small
          slot="note"
          class="note">
          {{ $t('companyForm.fillThisFieldToChangePassword') }}
        </small>
      </field>

      <field
        v-if="user.password"
        slot="content">
        <label slot="label" for="user_password_retype">{{ $t('companyForm.passwordConfirmation') }}</label>
        <input
          slot="field"
          name="user_password_retype"
          v-model="user.passwordRetype"
          :class="{ 'is-invalid': this.errors.indexOf('user_password') > -1 }"
          @keyup="clearError('user_password')"
          autocomplete="off"
          spellcheck="false"
          type="password" />
        <small
          slot="note"
          class="note">
          {{ $t('companyForm.fillThisFieldToChangePassword') }}
        </small>
      </field>
    </block>

    <block
      :title="$t('companyForm.timezone')"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label" for="timezone">{{ $t('companyForm.timezone') }}</label>
        <select
          slot="field"
          v-model="company.timezoneArea1"
          @change="company.timezoneArea2 = ''">
          <option
            v-for="(area, index) of timezoneAreas1"
            :key="'timezone-area-1-' + index"
            :value="area">
            {{ area }}
          </option>
        </select>

        <select
          v-if="company.timezoneArea1 && timezoneAreas2(company.timezoneArea1).length"
          slot="field"
          v-model="company.timezoneArea2">
          <option value="">{{ $t('companyForm.selectRegion') }}</option>
          <option
            v-for="(area, index) of timezoneAreas2(company.timezoneArea1)"
            :key="'timezone-area-2-' + index"
            :value="area">
            {{ area }}
          </option>
        </select>
      </field>
    </block>

    <block
      :title="$t('companyForm.companyData')"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label" for="company_name">{{ $t('companyForm.companyName') }}</label>
        <input
          slot="field"
          name="company_name"
          v-model="company.title"
          :class="{ 'is-invalid': this.errors.indexOf('company_title') > -1 }"
          @keyup="clearError('company_title')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="company_address">{{ $t('companyForm.address') }}</label>
        <input
          slot="field"
          name="company_address"
          v-model="company.address"
          :class="{ 'is-invalid': this.errors.indexOf('company_address') > -1 }"
          @keyup="clearError('company_address')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="company_postal_code">{{ $t('companyForm.postalCode') }}</label>
        <input
          slot="field"
          name="company_postal_code"
          v-model="company.postCode"
          :class="{ 'is-invalid': this.errors.indexOf('company_postCode') > -1 }"
          @keyup="clearError('company_postCode')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="company_city">{{ $t('companyForm.city') }}</label>
        <input
          slot="field"
          name="company_city"
          v-model="company.city"
          :class="{ 'is-invalid': this.errors.indexOf('company_city') > -1 }"
          @keyup="clearError('company_city')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="company_phone">{{ $t('companyForm.phone') }}</label>
        <input
          slot="field"
          name="company_phone"
          v-model="company.phone"
          :class="{ 'is-invalid': this.errors.indexOf('company_phone') > -1 }"
          @keyup="clearError('company_phone')"
          autocomplete="off"
          type="tel" />
      </field>

      <field slot="content">
        <label slot="label" for="company_email">{{ $t('companyForm.email') }}</label>
        <input
          slot="field"
          name="company_email"
          v-model="company.email"
          :class="{ 'is-invalid': this.errors.indexOf('company_email') > -1 }"
          @keyup="clearError('company_email')"
          autocomplete="off"
          type="email" />
        <small
          class="note"
          slot="note">
          {{ $t('companyForm.thisEmailWillBeUsedToGetNotificationsAboutNewReservations') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">{{ $t('companyForm.receiveNotifications') }}</label>
        <switcher
          v-model="company.receive_notifications"
          key="company-receive-notifications-switcher"
          slot="field" />
        <small
          class="note"
          slot="note">
          {{ $t('companyForm.receiveNotificationsDesc') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label" for="company_www">{{ $t('companyForm.www') }}</label>
        <input
          slot="field"
          name="company_www"
          v-model="company.www"
          autocomplete="off"
          :class="{ 'is-invalid': this.errors.indexOf('company_www') > -1 }"
          @keyup="clearError('company_www')"
          type="url" />
          <small
            class="note"
            slot="note"
            v-pure-html="$t('companyForm.wwwDescription')">
          </small>
      </field>

      <field slot="content">
        <label slot="label">{{ $t('companyForm.iWantInvoice') }}</label>
        <switcher
          v-model="company.invoiceData"
          key="company-invoice-data-switcher"
          slot="field" />
      </field>
    </block>

    <block
      v-if="company.invoiceData"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress"
      title="Dane do faktury">
      <field slot="content">
        <label slot="label" for="invoice_country">{{ $t('companyForm.companyCountry') }}</label>
        <select
          v-model="invoice.country"
          slot="field"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_country') > -1 }"
          @change="clearError('invoice_country');"
          autocomplete="off">
          <option :value="''">
            {{ $t('companyForm.selectCountry') }}
          </option>
          <option
            v-for="(country, code) in countriesList"
            :key="'country-' + code"
            :value="code">
            {{ country }}
          </option>
        </select>
      </field>

      <field slot="content">
        <label slot="label" for="invoice_nip">{{ $t('companyForm.taxID') }}</label>
        <input
          slot="field"
          name="invoice_nip"
          v-model="invoice.nip"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_nip') > -1 }"
          @keyup="clearError('invoice_nip')"
          autocomplete="off"
          type="text" />
        <small
          v-if="invoice.country === 'PL'"
          slot="note"
          class="note">
          {{ $t('companyForm.nipRule') }}
        </small>
        <btn
          v-if="invoice.country === 'PL'"
          slot="field"
          type="success medium"
          :disabled="!invoice.nip.match(/^[0-9]{10,10}$/) || checkingVATIDinProgress"
          @click.native="checkVatID">
          <template v-if="checkingVATIDinProgress">
            {{ $t('companyForm.vatIDCheckInProgress') }}
          </template>
          <template v-else>
            {{ $t('companyForm.applyVatID') }}
          </template>
        </btn>
      </field>

      <field slot="content">
        <label slot="label" for="invoice_title">{{ $t('companyForm.companyName') }}</label>
        <input
          slot="field"
          :readonly="invoice.country === 'PL'"
          :disabled="invoice.country === 'PL'"
          name="invoice_title"
          v-model="invoice.title"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_title') > -1 }"
          @keyup="clearError('invoice_title')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="invoice_address">{{ $t('companyForm.address') }}</label>
        <input
          slot="field"
          :readonly="invoice.country === 'PL'"
          :disabled="invoice.country === 'PL'"
          name="invoice_address"
          v-model="invoice.address"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_address') > -1 }"
          @keyup="clearError('invoice_address')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="invoice_postal_code">{{ $t('companyForm.postalCode') }}</label>
        <input
          slot="field"
          :readonly="invoice.country === 'PL'"
          :disabled="invoice.country === 'PL'"
          name="invoice_postal_code"
          v-model="invoice.postCode"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_postCode') > -1 }"
          @keyup="clearError('invoice_postCode')"
          autocomplete="off"
          type="text" />
      </field>

      <field slot="content">
        <label slot="label" for="invoice_city">{{ $t('companyForm.city') }}</label>
        <input
          slot="field"
          :readonly="invoice.country === 'PL'"
          :disabled="invoice.country === 'PL'"
          name="invoice_city"
          v-model="invoice.city"
          :class="{ 'is-invalid': this.errors.indexOf('invoice_city') > -1 }"
          @keyup="clearError('invoice_city')"
          autocomplete="off"
          type="text" />
      </field>
    </block>

    <block
      :title="$t('companyForm.logoTitle')"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress">
      <div
        v-if="!!company.logoUrl"
        slot="content"
        class="site-logo"
        :style="'background-image: url(\'' + company.logoUrl + '\')'">
        <small
          slot="content"
          class="remove-logo"
          @click="removeLogo">
          {{ $t('companyForm.removeLogo') }}
        </small>
      </div>

      <field slot="content">
        <label slot="label">
          {{ $t('companyForm.logo') }}
        </label>

        <input
          slot="field"
          type="file"
          :accept="$allowedImageFiles"
          :class="{ 'is-invalid': errors.indexOf('logo_file_ext') > -1 }"
          @change="setNewLogo" />
        <small
          slot="note"
          class="note">
          <template v-if="errors.indexOf('logo_file_ext') > -1">{{ $t('companyForm.invalidFileFormat') }}</template>
          {{ $t('companyForm.supportedFormats') }}
        </small>
        <small
          v-if="!!company.logo"
          class="note"
          slot="note">
          {{ $t('companyForm.submitNewFileToChangeLogo') }}
        </small>
      </field>
    </block>

    <block
      :title="$t('companyForm.openingHours')"
      :isLoading="!dataLoaded"
      :isSaving="saveInProgress">
      <work-hours
        slot="content"
        ref="work-hours"
        :useCompanyHours="false"
        :initialDays="hours.days"
        :initialDaysHours="hours.daysHours" />
    </block>
  </div>
</template>

<script>
import AppConfig from './../../env.js';
import countries from './../../data/countries.js';
import Utils from './../helpers/Utils';
import Block from './../common/Block';
import BlockListHeading from './../common/BlockListHeading';
import ErrorUtils from './../helpers/ErrorUtils.js';
import FormHelpers from './../mixins/FormHelpers';
import StickyHeader from './../common/StickyHeader';
import WorkHours from './../common/WorkHours';
import Vue from 'vue';

export default {
  name: 'company-form',
  mixins: [
    FormHelpers
  ],
  components: {
    'block': Block,
    'block-list-heading': BlockListHeading,
    'sticky-header': StickyHeader,
    'work-hours': WorkHours
  },
  computed: {
    timezoneAreas1 () {
      let areas = this.$store.state.config.timezones.map(timezone => timezone.split('/')[0]);
      areas = [...new Set(areas)];
      return areas;
    },
    timezone () {
      return [this.company.timezoneArea1, this.company.timezoneArea2].filter(value => !!value).join('/');
    },
    countriesList () {
      return countries[AppConfig.uiLanguage];
    }
  },
  watch: {
    'invoice.country': function (newValue, oldValue) {
      if (newValue === 'PL' && this.originalVatID !== this.invoice.nip) {
        this.invoice.title = '';
        this.invoice.address = '';
        this.invoice.postCode = '';
        this.invoice.city = '';
      }
    }
  },
  data () {
    return {
      errors: [],
      user: {
        name: '',
        email: '',
        phone: '',
        password: '',
        passwordRetype: '',
        confirmed: false
      },
      company: {
        address: '',
        city: '',
        email: '',
        receive_notifications: 1,
        logo: '',
        logoUrl: '',
        phone: '',
        postCode: '',
        title: '',
        www: '',
        invoiceData: false,
        timezoneArea1: '',
        timezoneArea2: ''
      },
      invoice: {
        country: '',
        nip: '',
        title: '',
        address: '',
        postCode: '',
        city: ''
      },
      hours: {
        days: [false, false, false, false, false, false, false],
        daysHours: []
      },
      selectedFile: '',
      dataLoaded: false,
      saveInProgress: false,
      checkingVATIDinProgress: false,
      originalVatID: ''
    };
  },
  mounted () {
    if (this.$store.state.config.user.email) {
      this.loadData();
    }

    this.originalVatID = this.$store.state.config.company.invoice_nip;
    this.$bus.$on('configuration-loaded', this.loadData);
  },
  methods: {
    setNewLogo (event) {
      this.selectedFile = event.target.files[0];
      this.clearError('logo_file_ext');
    },
    loadData () {
      this.user.name = this.$store.state.config.user.name;
      this.user.email = this.$store.state.config.user.email;
      this.user.phone = this.$store.state.config.user.phone;
      this.user.confirmed = this.$store.state.config.user.confirmed_at;

      this.company.address = this.$store.state.config.company.address;
      this.company.city = this.$store.state.config.company.city;
      this.company.contactPerson = this.$store.state.config.company.contact_person;
      this.company.email = this.$store.state.config.company.email;
      this.company.receive_notifications = this.$store.state.config.company.receive_notifications;
      this.company.phone = this.$store.state.config.company.phone;
      this.company.postCode = this.$store.state.config.company.post_code;
      this.company.title = this.$store.state.config.company.title;
      this.company.www = this.$store.state.config.company.www || '';
      this.company.invoiceData = this.$store.state.config.company.invoice_data;
      this.company.logo = this.$store.state.config.company.logo;
      this.company.logoUrl = this.$store.state.config.company.logo_url;

      this.invoice.country = this.$store.state.config.company.invoice_country;
      this.invoice.nip = this.$store.state.config.company.invoice_nip;
      this.invoice.title = this.$store.state.config.company.invoice_title;
      this.invoice.address = this.$store.state.config.company.invoice_address;
      this.invoice.postCode = this.$store.state.config.company.invoice_post_code;
      this.invoice.city = this.$store.state.config.company.invoice_city;

      if (this.$store.state.config.company.timezone) {
        this.company.timezoneArea1 = this.$store.state.config.company.timezone.split('/')[0];
        this.company.timezoneArea2 = this.$store.state.config.company.timezone.split('/').slice(1).join('/');
      } else {
        this.company.timezoneArea1 = '';
        this.company.timezoneArea2 = '';
      }

      this.hours.days = this.getActiveDays();
      this.hours.daysHours = this.getHours();
      this.selectedFile = '';
      this.dataLoaded = true;
    },
    getActiveDays () {
      let days = [];

      for (let i = 1; i <= 7; i++) {
        days.push(!!this.$store.state.config.company['opening_hours_' + i]);
      }

      return days;
    },
    getHours () {
      let hours = [[], [], [], [], [], [], []];

      for (let i = 1; i <= 7; i++) {
        if (this.$store.state.config.company['opening_hours_' + i]) {
          let hourFrom = this.$store.state.config.company['opening_hours_' + i + '_from'];
          let hourTo = this.$store.state.config.company['opening_hours_' + i + '_to'];

          if (hourFrom.split(':')[0].length === 1) {
            hourFrom = '0' + hourFrom;
          }

          if (hourTo.split(':')[0].length === 1) {
            hourTo = '0' + hourTo;
          }

          hours[i - 1].push([
            hourFrom,
            hourTo
          ]);

          if (this.$store.state.config.company.additional_hours && this.$store.state.config.company.additional_hours[i]) {
            for (let j = 0; j < this.$store.state.config.company.additional_hours[i].length; j++) {
              hours[i - 1].push([
                this.$store.state.config.company.additional_hours[i][j].open_from,
                this.$store.state.config.company.additional_hours[i][j].open_to
              ]);
            }
          }
        } else {
          let emptyHours = this.getFirstHour(i);

          hours[i - 1].push([
            emptyHours,
            emptyHours
          ]);
        }
      }

      return hours;
    },
    getFirstHour (dayIndex) {
      if (!this.$store.state.config.company['opening_hours_' + dayIndex]) {
        return '00:00';
      } else {
        let hour = this.$store.state.config.company['opening_hours_' + dayIndex + '_from'];

        if (hour.split(':')[0].length === 1) {
          hour = '0' + hour;
        }

        return hour;
      }
    },
    validate () {
      this.errors = [];

      if (this.user.name.trim() === '') {
        this.errors.push('user_name');
      }

      if (this.user.email.trim() === '') {
        this.errors.push('user_email');
      }

      if (this.user.password !== '' && this.user.password !== this.user.passwordRetype) {
        this.errors.push('user_password');
      } else {
        this.clearError('user_password');
      }

      let companyFields = ['title', 'address', 'postCode', 'city', 'phone', 'email'];
      let invoiceFields = ['title', 'country', 'nip', 'address', 'postCode', 'city'];

      for (let i = 0; i < companyFields.length; i++) {
        if (!this.company[companyFields[i]] || this.company[companyFields[i]].trim() === '') {
          this.errors.push('company_' + companyFields[i]);
        }
      }

      if (this.company.invoiceData) {
        for (let i = 0; i < invoiceFields.length; i++) {
          if (!this.invoice[invoiceFields[i]].trim()) {
            this.errors.push('invoice_' + invoiceFields[i]);
          }
        }

        if (this.invoice.country === 'PL' && !this.invoice.nip.match(/^[0-9]{10,10}$/)) {
          this.errors.push('invoice_nip');
        }
      }

      if (
        this.selectedFile &&
        !this.isAllowedFileExt(this.selectedFile.name)
      ) {
        this.errors.push('logo_file_ext');
      }

      if (
        (
          this.company.www &&
          this.company.www.trim() !== '' &&
          this.company.www.indexOf('http://') === -1 &&
          this.company.www.indexOf('https://') === -1
        )
      ) {
        this.errors.push('company_www');
      }
    },
    save () {
      this.validate();

      if (this.errors.length) {
        this.$bus.$emit('notification-display', {
          message: this.$t('common.ui.invalidFields'),
          type: 'error'
        });

        return;
      } else {
        this.saveInProgress = true;
      }

      this.saveCompanyData();
    },
    saveCompanyData () {
      let companyData = this.prepareCompanyData();

      if (this.selectedFile === '') {
        this.$http.post('/panel/general/save-configuration', companyData).then(response => {
          if (response.data.success) {
            this.successfulSave(response, companyData, false);
          } else {
            this.$bus.$emit('notification-display', {
              message: this.$t('common.ui.invalidFields'),
              type: 'error'
            });
          }

          this.saveInProgress = false;
        }).catch((error) => {
          if (error.response) {
            if (error.response.data.errors && error.response.data.errors['user.email'] && error.response.data.errors['user.email'][0].indexOf('taken') > -1) {
              this.errors.push('user_email_taken');
            } else if (error.response.data.errors && error.response.data.errors['user.email'] && error.response.data.errors['user.email'][0].indexOf('valid') > -1) {
              this.errors.push('user_email');
            }

            this.$bus.$emit('notification-display', {
              message: this.$t('common.ui.invalidFields'),
              type: 'error'
            });
          }

          ErrorUtils.handleSubmitError(error, '/panel/general/save-configuration', this.$router, this.$bus, this.saveCompanyData.bind(this));
          this.saveInProgress = false;
        });
      } else {
        companyData.site_logo = this.selectedFile;
        this.$http.request({
          method: 'POST',
          url: '/panel/general/save-configuration',
          headers: { 'Content-Type': undefined },
          transformRequest: function (data) {
            let dataToSend = Utils.objectToFormData(companyData);
            dataToSend.append('logo', data.site_logo);
            return dataToSend;
          },
          data: companyData
        }).then(response => {
          this.successfulSave(response, companyData, true);
          this.saveInProgress = false;
        }).catch(error => {
          ErrorUtils.handleSubmitError(error, '/panel/general/save-configuration', this.$router, this.$bus, this.saveCompanyData.bind(this));
          this.saveInProgress = false;
        });
      }
    },
    successfulSave (response, companyData, reloadFullConfig = false) {
      if (response.data.success) {
        this.$bus.$emit('notification-display', {
          message: this.$t('companyForm.companyDataSaved'),
          type: 'success'
        });

        Vue.set(this, 'originalVatID', companyData.company.invoice_nip);

        if (reloadFullConfig) {
          this.$store.commit('config/refreshConfig', companyData);
          this.$http.post('/panel/general/get-configuration').then(response => {
            this.$store.commit('config/setConfig', response.data);
            this.loadData();
          }).catch(error => {
            ErrorUtils.handleSubmitError(error, '/panel/general/get-configuration', this.$router, this.$bus, this.successfulSave.bind(this, response, companyData, reloadFullConfig));
          });
        } else {
          if (companyData.user.password) {
            Vue.delete(companyData.user, 'password');
            Vue.delete(companyData.user, 'password_confirmation');
          }

          this.$store.commit('config/refreshConfig', companyData);
        }
      }
    },
    prepareCompanyData () {
      let companyData = {
        user: {
          name: this.user.name,
          email: this.user.email,
          phone: this.user.phone
        },
        company: {
          title: this.company.title,
          address: this.company.address,
          post_code: this.company.postCode,
          city: this.company.city,
          phone: this.company.phone,
          email: this.company.email,
          receive_notifications: this.company.receive_notifications,
          www: this.company.www,
          invoice_data: this.company.invoiceData ? 1 : 0,
          timezone: this.timezone
        }
      };

      if (this.user.password) {
        companyData.user.password = this.user.password;
        companyData.user.password_confirmation = this.user.passwordRetype;
      }

      if (this.company.invoiceData) {
        companyData.company.invoice_title = this.invoice.title;
        companyData.company.invoice_nip = this.invoice.nip;
        companyData.company.invoice_address = this.invoice.address;
        companyData.company.invoice_city = this.invoice.city;
        companyData.company.invoice_post_code = this.invoice.postCode;
        companyData.company.invoice_country = this.invoice.country;
      }

      this.days = this.$refs['work-hours'].days.slice();
      this.daysHours = this.$refs['work-hours'].getHours();

      for (let i = 0; i < 7; i++) {
        if (this.days[i]) {
          companyData.company['opening_hours_' + (i + 1)] = 1;
          companyData.company['opening_hours_' + (i + 1) + '_from'] = this.daysHours[i][0][0];
          companyData.company['opening_hours_' + (i + 1) + '_to'] = this.daysHours[i][0][1];
        } else {
          companyData.company['opening_hours_' + (i + 1)] = 0;
          companyData.company['opening_hours_' + (i + 1) + '_from'] = null;
          companyData.company['opening_hours_' + (i + 1) + '_to'] = null;
        }

        if (this.daysHours[i].length > 1) {
          if (!companyData.company['additional_hours']) {
            companyData.company['additional_hours'] = {};
          }

          if (!companyData.company['additional_hours'][i + 1]) {
            companyData.company['additional_hours'][i + 1] = [];
          }

          for (let j = 1; j < this.daysHours[i].length; j++) {
            companyData.company['additional_hours'][i + 1].push({
              'open_from': this.daysHours[i][j][0],
              'open_to': this.daysHours[i][j][1]
            });
          }
        }
      }

      return companyData;
    },
    removeLogo () {
      this.$http.post('/panel/general/remove-company-logo').then(response => {
        this.company.logoUrl = '';
        this.$store.commit('config/removeCompanyLogo');
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/remove-company-logo', this.$router, this.$bus, this.removeLogo.bind(this));
      });
    },
    isAllowedFileExt (fileName) {
      if (
        fileName.substr(-4) === '.jpg' ||
        fileName.substr(-4) === '.png' ||
        fileName.substr(-5) === '.jpeg' ||
        fileName.substr(-4) === '.JPG' ||
        fileName.substr(-4) === '.PNG' ||
        fileName.substr(-5) === '.JPEG'
      ) {
        return true;
      }

      return false;
    },
    timezoneAreas2 (area) {
      let areas = this.$store.state.config.timezones.filter(timezone => timezone.split('/')[0] === area);
      areas = areas.map(timezone => timezone.split('/').slice(1).join('/'));
      areas = areas.filter(timezone => !!timezone);
      return areas;
    },
    checkVatID () {
      if (this.invoice.country === 'PL' && !this.invoice.nip.match(/^[0-9]{10,10}$/)) {
        this.$bus.$emit('alert-display', {
          title: this.$t('companyForm.invalidVATID'),
          desc: this.$t('companyForm.invalidVATIDDesc'),
          okLabel: this.$t('common.ui.ok'),
          okClick: () => true,
          isDanger: false
        });
        return;
      }

      this.checkingVATIDinProgress = true;

      this.$http.post('/panel/general/nip-24-company-data', {
        nip: this.invoice.nip
      }).then(response => {
        if (!response.data.success) {
          this.$bus.$emit('alert-display', {
            title: this.$t('companyForm.invalidVATID'),
            desc: this.$t('companyForm.invalidVATID2Desc'),
            okLabel: this.$t('common.ui.ok'),
            okClick: () => true,
            isDanger: false
          });
        } else {
          this.invoice.title = response.data.data.name;
          this.invoice.address = response.data.data.street + ' ' + response.data.data.street_number;

          if (response.data.data.house_number) {
            this.invoice.address += ' / ' + response.data.data.house_number;
          }

          this.invoice.postCode = response.data.data.post_code.substr(0, 2) + '-' + response.data.data.post_code.substr(2, 3);
          this.invoice.city = response.data.data.post_city;
          Vue.set(this, 'originalVatID', this.invoice.nip);
        }

        this.checkingVATIDinProgress = false;
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/general/nip-24-company-data', this.$router, this.$bus);
        this.checkingVATIDinProgress = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('configuration-loaded', this.loadData);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.company-data {
  .site-logo {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: auto 100%;
    height: 56px;
    margin: 10px 0 50px 0;
    position: relative;
    width: 100%;
  }

  .remove-logo {
    bottom: -50px;
    color: $color-danger;
    cursor: pointer;
    display: block;
    font-size: 1.2rem;
    left: 50%;
    margin: 1rem 0;
    position: absolute;
    text-align: center;
    text-decoration: underline;
    transform: translateX(-50%);
    width: 150px;
  }

  select + select {
    margin-left: 1rem;
    width: calc((100% / 3) - 1rem);
  }

  select.is-invalid {
    border: 1px solid $color-danger;
  }
}

@media (max-width: 640px) {
  .company-data {
    margin-top: 6rem;
    .field {
      input,
      select,
      textarea {
        width: 66%;
      }
    }

    select + select {
      margin-left: calc(100% / 3);
      margin-top: 1rem;
    }
  }
}

@media (max-width: 580px) {
  .company-data {
    .field {
      label,
      input,
      select,
      textarea {
        text-align: left;
        width: 100%;
      }

      label {
        margin-bottom: .5rem;
      }

      small.note {
        padding: .5rem 0 0 0;

        &.error {
          color: $color-danger;
        }
      }
    }

    select + select {
      margin-left: 0;
      margin-top: 1rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/CompanyForm.vue